import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  CalendarDays, Clock, User, Users, Loader2, ChevronLeft, ChevronRight,
  LogIn, ExternalLink, CheckCircle, AlertCircle
} from 'lucide-react';
import { format, addDays, startOfWeek, isSameDay, parseISO } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';
import { formatApiError } from '../lib/apiError';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const WidgetPage = () => {
  const { studioId } = useParams();
  const [settings, setSettings] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Calendar state
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  
  // Booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [userToken, setUserToken] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [userPasses, setUserPasses] = useState([]);
  const [bookingStep, setBookingStep] = useState('login'); // login, options, confirm, success, waitlist_success
  const [isWaitlistMode, setIsWaitlistMode] = useState(false);

  const fetchWidgetData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/widget/${studioId}/settings`);
      setSettings(data);

      if (!data.enabled) {
        setError('This booking widget is not currently available.');
      }
    } catch (e) {
      setError('Unable to load booking widget.');
    } finally {
      setLoading(false);
    }
  }, [studioId]);

  const fetchClasses = useCallback(async () => {
    try {
      const startDate = currentWeekStart.toISOString();
      const endDate = addDays(currentWeekStart, 7).toISOString();

      const { data } = await axios.get(`${API_URL}/api/widget/${studioId}/classes`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setClasses(data.classes || []);
    } catch (e) {
      console.error('Failed to fetch classes:', e);
    }
  }, [studioId, currentWeekStart]);

  useEffect(() => {
    fetchWidgetData();
  }, [fetchWidgetData]);

  useEffect(() => {
    if (currentWeekStart) {
      fetchClasses();
    }
  }, [fetchClasses, currentWeekStart]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, loginForm);
      setUserToken(data.access_token);
      setUserInfo(data.user);
      setIsLoggedIn(true);
      
      // Fetch user's class passes for this studio
      const passesRes = await axios.get(`${API_URL}/api/user-class-passes`, {
        headers: { Authorization: `Bearer ${data.access_token}` }
      });
      const studioPasses = passesRes.data.filter(p => p.studio_id === studioId && p.status === 'active');
      setUserPasses(studioPasses);
      
      // Go to waitlist step if class is full, otherwise options step
      setBookingStep(isWaitlistMode ? 'waitlist' : 'options');
    } catch (e) {
      toast.error(formatApiError(e, 'Login failed'));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleBookClass = async (useClassPass = false, passId = null) => {
    if (!selectedClass || !userToken) return;
    
    setBookingLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/bookings`,
        {
          class_id: selectedClass.id,
          studio_id: studioId,
          use_class_pass: useClassPass,
          class_pass_id: passId
        },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      
      // Update local pass count immediately if used a class pass
      if (useClassPass && passId) {
        setUserPasses(prev => prev.map(p => 
          p.id === passId 
            ? { ...p, passes_remaining: Math.max(0, p.passes_remaining - 1) }
            : p
        ));
      }
      
      setBookingStep('success');
      fetchClasses(); // Refresh classes
    } catch (e) {
      toast.error(formatApiError(e, 'Booking failed'));
    } finally {
      setBookingLoading(false);
    }
  };

  const handleJoinWaitlist = async () => {
    if (!selectedClass || !userToken) return;
    
    setBookingLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/waitlist`,
        {
          class_id: selectedClass.id,
          studio_id: studioId
        },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      
      setBookingStep('waitlist_success');
      fetchClasses(); // Refresh classes
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to join waitlist'));
    } finally {
      setBookingLoading(false);
    }
  };

  const openBookingModal = (cls, waitlistMode = false) => {
    setSelectedClass(cls);
    setIsWaitlistMode(waitlistMode);
    setBookingStep(isLoggedIn ? (waitlistMode ? 'waitlist' : 'options') : 'login');
    setShowBookingModal(true);
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setSelectedClass(null);
    setIsWaitlistMode(false);
    if (bookingStep === 'success' || bookingStep === 'waitlist_success') {
      setBookingStep(isLoggedIn ? 'options' : 'login');
    }
  };

  const goToFullSite = () => {
    window.open(`${API_URL.replace('/api', '')}/login`, '_blank');
  };

  // Generate days of the week
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  // Group classes by day
  const classesByDay = weekDays.map(day => ({
    date: day,
    classes: classes.filter(c => isSameDay(parseISO(c.start_time), day))
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4" style={{ backgroundColor: '#f9fafb' }}>
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <p className="text-lg font-medium mb-2">Widget Unavailable</p>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Custom styles from settings
  const customStyles = {
    '--widget-bg': settings?.background_color || '#ffffff',
    '--widget-text': settings?.text_color || '#1f2937',
    '--widget-button': settings?.button_color || '#3b82f6',
    '--widget-button-text': settings?.button_text_color || '#ffffff',
    '--widget-accent': settings?.accent_color || '#10b981',
    '--widget-radius': settings?.border_radius || '8px',
  };

  return (
    <div 
      className="min-h-screen p-4"
      style={{ 
        backgroundColor: settings?.background_color || '#ffffff',
        color: settings?.text_color || '#1f2937',
        fontFamily: settings?.font_family || 'system-ui',
        ...customStyles
      }}
    >
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <h1 className="text-2xl font-bold mb-1">{settings?.studio_name || 'Class Schedule'}</h1>
        <p className="text-sm opacity-70">Book your next class</p>
      </div>

      {/* Week Navigation */}
      <div className="max-w-4xl mx-auto mb-4 flex items-center justify-between">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setCurrentWeekStart(addDays(currentWeekStart, -7))}
          style={{ borderColor: settings?.button_color }}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>
        <span className="font-medium">
          {format(currentWeekStart, 'MMM d')} - {format(addDays(currentWeekStart, 6), 'MMM d, yyyy')}
        </span>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setCurrentWeekStart(addDays(currentWeekStart, 7))}
          style={{ borderColor: settings?.button_color }}
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-7 gap-2">
          {classesByDay.map(({ date, classes: dayClasses }) => (
            <div key={date.toISOString()} className="min-h-[200px]">
              {/* Day Header */}
              <div 
                className="text-center p-2 rounded-t font-medium text-sm"
                style={{ 
                  backgroundColor: isSameDay(date, new Date()) ? settings?.accent_color : 'transparent',
                  color: isSameDay(date, new Date()) ? '#ffffff' : settings?.text_color
                }}
              >
                <div>{format(date, 'EEE')}</div>
                <div className="text-lg">{format(date, 'd')}</div>
              </div>
              
              {/* Classes */}
              <div className="space-y-1 mt-1">
                {dayClasses.length === 0 ? (
                  <p className="text-xs text-center opacity-50 py-4">No classes</p>
                ) : (
                  dayClasses.map(cls => {
                    const isFull = cls.spots_remaining <= 0;
                    return (
                      <div
                        key={cls.id}
                        className="w-full text-left p-2 rounded text-xs"
                        style={{ 
                          backgroundColor: settings?.button_color + '15',
                          borderLeft: `3px solid ${isFull ? '#f97316' : settings?.button_color}`,
                          borderRadius: settings?.border_radius
                        }}
                      >
                        <div className="font-medium truncate">{cls.name}</div>
                        <div className="opacity-70">{format(parseISO(cls.start_time), 'h:mm a')}</div>
                        {settings?.show_instructor && cls.instructor_name && (
                          <div className="opacity-60 truncate">{cls.instructor_name}</div>
                        )}
                        {settings?.show_spots_remaining && (
                          <div 
                            className="mt-1 text-xs"
                            style={{ color: cls.spots_remaining <= 2 ? '#ef4444' : settings?.accent_color }}
                          >
                            {isFull ? 'Class Full' : `${cls.spots_remaining} spots`}
                          </div>
                        )}
                        {/* Action Button */}
                        <button
                          onClick={() => openBookingModal(cls, isFull)}
                          className="w-full mt-2 py-1.5 px-2 rounded text-xs font-medium transition-all hover:opacity-90"
                          style={{ 
                            backgroundColor: isFull ? '#f97316' : settings?.button_color,
                            color: settings?.button_text_color,
                            borderRadius: settings?.border_radius
                          }}
                        >
                          {isFull ? 'Join Waitlist' : 'Book'}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={closeBookingModal}>
        <DialogContent className="max-w-md" style={{ backgroundColor: settings?.background_color, color: settings?.text_color }}>
          <DialogHeader>
            <DialogTitle>{selectedClass?.name}</DialogTitle>
            <DialogDescription style={{ color: settings?.text_color, opacity: 0.7 }}>
              {selectedClass && format(parseISO(selectedClass.start_time), 'EEEE, MMMM d • h:mm a')}
              {selectedClass?.instructor_name && ` • ${selectedClass.instructor_name}`}
            </DialogDescription>
          </DialogHeader>

          {/* Login Step */}
          {bookingStep === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4 py-4">
              <p className="text-sm opacity-80">
                Please log in to {isWaitlistMode ? 'join the waitlist for' : 'book'} this class
              </p>
              {isWaitlistMode && (
                <div className="p-3 rounded-lg border border-orange-200 bg-orange-50">
                  <p className="text-xs text-orange-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    This class is full - you'll be added to the waitlist
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input 
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="••••••••"
                  required
                />
              </div>
              <DialogFooter className="flex-col gap-2 sm:flex-col">
                <Button 
                  type="submit" 
                  disabled={loginLoading}
                  className="w-full"
                  style={{ backgroundColor: isWaitlistMode ? '#f97316' : settings?.button_color, color: settings?.button_text_color }}
                >
                  {loginLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
                  Log In & {isWaitlistMode ? 'Join Waitlist' : 'Book'}
                </Button>
                <Button type="button" variant="ghost" onClick={goToFullSite} className="w-full">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Go to Full Website
                </Button>
              </DialogFooter>
            </form>
          )}

          {/* Options Step */}
          {bookingStep === 'options' && (
            <div className="space-y-4 py-4">
              <p className="text-sm opacity-80">Hi {userInfo?.first_name}! Choose how to book:</p>
              
              {/* ClassPass Options */}
              {userPasses.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Use a ClassPass:</p>
                  {userPasses.map(pass => (
                    <Button
                      key={pass.id}
                      variant="outline"
                      className="w-full justify-between"
                      onClick={() => handleBookClass(true, pass.id)}
                      disabled={bookingLoading || pass.passes_remaining <= 0}
                    >
                      <span>{pass.class_pass_name}</span>
                      <Badge variant="secondary">{pass.passes_remaining} left</Badge>
                    </Button>
                  ))}
                </div>
              ) : (
                <p className="text-sm opacity-70">No active ClassPasses for this studio</p>
              )}

              {/* Pay Single Rate */}
              {selectedClass?.price > 0 && (
                <Button
                  className="w-full"
                  onClick={() => handleBookClass(false)}
                  disabled={bookingLoading}
                  style={{ backgroundColor: settings?.button_color, color: settings?.button_text_color }}
                >
                  {bookingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Pay €{selectedClass.price} (Single Class)
                </Button>
              )}

              <Button variant="ghost" onClick={goToFullSite} className="w-full">
                <ExternalLink className="w-4 h-4 mr-2" />
                Go to Full Website
              </Button>
            </div>
          )}

          {/* Waitlist Step */}
          {bookingStep === 'waitlist' && (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg border border-orange-200 bg-orange-50">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-orange-800">This class is currently full</p>
                    <p className="text-sm text-orange-700 mt-1">
                      Join the waitlist and we'll notify you if a spot becomes available.
                    </p>
                  </div>
                </div>
              </div>
              
              <p className="text-sm opacity-80">Hi {userInfo?.first_name}!</p>
              
              <Button
                className="w-full"
                onClick={handleJoinWaitlist}
                disabled={bookingLoading}
                style={{ backgroundColor: '#f97316', color: '#ffffff' }}
              >
                {bookingLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
                Join Waitlist
              </Button>

              <Button variant="ghost" onClick={goToFullSite} className="w-full">
                <ExternalLink className="w-4 h-4 mr-2" />
                Go to Full Website
              </Button>
            </div>
          )}

          {/* Success Step */}
          {bookingStep === 'success' && (
            <div className="py-8 text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: settings?.accent_color }} />
              <h3 className="text-xl font-bold mb-2">Booking Confirmed!</h3>
              <p className="opacity-70 mb-4">
                {selectedClass?.name}<br />
                {selectedClass && format(parseISO(selectedClass.start_time), 'EEEE, MMMM d • h:mm a')}
              </p>
              <Button onClick={closeBookingModal} style={{ backgroundColor: settings?.button_color, color: settings?.button_text_color }}>
                Done
              </Button>
            </div>
          )}

          {/* Waitlist Success Step */}
          {bookingStep === 'waitlist_success' && (
            <div className="py-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                <Users className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Added to Waitlist!</h3>
              <p className="opacity-70 mb-4">
                {selectedClass?.name}<br />
                {selectedClass && format(parseISO(selectedClass.start_time), 'EEEE, MMMM d • h:mm a')}
              </p>
              <p className="text-sm opacity-60 mb-4">
                We'll notify you when a spot becomes available.
              </p>
              <Button onClick={closeBookingModal} style={{ backgroundColor: '#f97316', color: '#ffffff' }}>
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <div className="max-w-4xl mx-auto mt-6 text-center text-xs opacity-50">
        Powered by StudioScheduler
      </div>
    </div>
  );
};

export default WidgetPage;
